/* The envelope, cursor and null-handling rules from 15-api-contract.md
 * sections 1.2-1.4. Every resource file in this directory paginates through
 * these helpers rather than reimplementing cursor logic per resource -
 * CLAUDE.md rule 2.
 */
import { useEffect } from "react";
import { queryOptions, useInfiniteQuery } from "@tanstack/react-query";

import { apiFetch } from "../auth";
import { refusalDetail } from "./refusal";
import type { AssessmentReading, Page } from "./types";

export const DEFAULT_LIMIT = 50;
export const MAX_LIMIT = 500;

/* `error` is 15 section 1.5's token for the status the real API answers with,
 * one per status, so a screen cannot come to depend on a cause the API never
 * names. */
export class ApiValidationError extends Error {
  readonly error: string;
  readonly detail: string;

  constructor(error: string, detail: string) {
    super(detail);
    this.error = error;
    this.detail = detail;
  }
}

/* An opaque cursor over a stably-sorted array: the position is encoded, never
 * the row itself, so a caller cannot construct or increment one (15 section
 * 1.3). Sort keys here are always a row's index in an already-sorted array,
 * which is enough to prove termination and page boundaries without a real
 * database's keyset comparison. */
interface CursorPayload {
  offset: number;
}

export function encodeCursor(offset: number): string {
  return btoa(JSON.stringify({ offset } satisfies CursorPayload));
}

/* A malformed cursor is a client error, never a silent restart from page one
 * (15 section 1.3). */
export function decodeCursor(cursor: string): number {
  try {
    const payload = JSON.parse(atob(cursor)) as CursorPayload;
    if (typeof payload.offset !== "number" || payload.offset < 0) {
      throw new Error("not a cursor");
    }
    return payload.offset;
  } catch {
    throw new ApiValidationError("bad_request", "The cursor could not be parsed.");
  }
}

export interface PageArgs {
  cursor?: string | null;
  limit?: number;
}

/* Slices an already-filtered, already-sorted array into one page of the
 * envelope. total is passed in rather than computed here because some
 * callers deliberately return null for it (15 section 1.4 - "too expensive
 * to promise"), which this generator never actually hits but the contract
 * still models. */
export function paginate<T>(
  sorted: T[],
  { cursor, limit }: PageArgs,
  total: number | null = sorted.length,
): Page<T> {
  const effectiveLimit = limit ?? DEFAULT_LIMIT;
  if (effectiveLimit > MAX_LIMIT) {
    throw new ApiValidationError(
      "unprocessable_entity",
      `limit must not exceed ${MAX_LIMIT}.`,
    );
  }

  const offset = cursor ? decodeCursor(cursor) : 0;
  const items = sorted.slice(offset, offset + effectiveLimit);
  const nextOffset = offset + effectiveLimit;
  const next_cursor = nextOffset < sorted.length ? encodeCursor(nextOffset) : null;

  return { items, next_cursor, total };
}

/* A query parameter's value. A list repeats the parameter once per item, which
 * is how the API takes a filter with several values. */
export type QueryParams = Record<string, string | string[] | undefined>;

/* One JSON body from the API, or the refusal's `detail` as the error, with
 * `failure` describing the refusal where the API gives no detail. */
export async function fetchJson<T>(path: string, failure: (status: number) => string): Promise<T> {
  return readJson<T>(await apiFetch(path), failure);
}

/* `body` sent as JSON, for a route that takes one rather than a query string,
 * read as `fetchJson` reads. `absent` is what a 404 answers instead of an
 * error, for a route where "none" is an answer rather than a failure. */
export async function postJson<T>(
  path: string,
  body: unknown,
  failure: (status: number) => string,
  absent?: { on404: T },
): Promise<T> {
  return sendJson<T>("POST", path, body, failure, absent);
}

/* `postJson` for a route that sets a value to what `body` says, which PUT
 * means: sending it twice leaves what sending it once does. */
export async function putJson<T>(path: string, body: unknown, failure: (status: number) => string): Promise<T> {
  return sendJson<T>("PUT", path, body, failure);
}

async function sendJson<T>(
  method: "POST" | "PUT",
  path: string,
  body: unknown,
  failure: (status: number) => string,
  absent?: { on404: T },
): Promise<T> {
  const response = await apiFetch(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (absent !== undefined && response.status === 404) return absent.on404;
  return readJson<T>(response, failure);
}

async function readJson<T>(response: Response, failure: (status: number) => string): Promise<T> {
  if (!response.ok) {
    throw new Error(await refusalDetail(response, failure(response.status)));
  }
  return (await response.json()) as T;
}

/* `path` with `params` as its query string, a list repeating its parameter. */
export function withQuery(path: string, params: QueryParams): string {
  const query = new URLSearchParams();
  for (const [name, value] of Object.entries(params)) {
    if (value === undefined) continue;
    for (const item of Array.isArray(value) ? value : [value]) query.append(name, item);
  }
  const search = query.toString();
  return search ? `${path}?${search}` : path;
}

/* One page of a real collection, or the refusal's `detail` as the error. The
 * cursor goes back exactly as the API sent it - never built or read here
 * (15 section 1.3). `limit` is the largest page unless a screen pages by hand. */
export async function fetchPage<T>(
  path: string,
  params: QueryParams,
  cursor: string | null,
  limit: number = MAX_LIMIT,
): Promise<Page<T>> {
  const query: QueryParams = { limit: String(limit), ...params };
  if (cursor !== null) query.cursor = cursor;
  return fetchJson<Page<T>>(withQuery(path, query), (status) => `${path} could not be read (${status})`);
}

/* The tree or pole a Kanopy viewer selected, from its list: the row seen as
 * the instance within the capture the viewer shows (`app/reads.py`'s
 * `instance_id` filter), or null where it is no row of ours. Query options
 * rather than a hook, because a selection is an event and the screen asks
 * from its handler, through the query cache. */
export function atInstanceQuery<T>(path: string, captureId: string, instanceId: number) {
  return queryOptions({
    queryKey: ["mirror", path, "at-instance", captureId, instanceId],
    queryFn: async () => {
      const params = { capture_id: captureId, instance_id: String(instanceId) };
      return (await fetchPage<T>(path, params, null, 1)).items[0] ?? null;
    },
  });
}

/* Every page of a real collection, for a screen that needs the whole of it -
 * the map draws every capture, not the first page of them.
 *
 * `useInfiniteQuery` is the cursor path (`CLAUDE.md` §4), and the effect only
 * asks it for the next page; the fetch, its cache and its dedup stay
 * TanStack's, which is what the rule against fetching in an effect protects.
 * `items` is empty until the last page lands, so a screen never draws a
 * collection it is still reading as though it were complete. */
export function useAllPages<T>(
  key: readonly unknown[],
  path: string,
  params: QueryParams,
  enabled = true,
): { items: T[] | undefined; error: Error | null } {
  const query = useInfiniteQuery({
    queryKey: [...key, params],
    queryFn: ({ pageParam }) => fetchPage<T>(path, params, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    enabled,
  });
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const done = query.data !== undefined && !hasNextPage;
  return {
    items: done ? query.data.pages.flatMap((p) => p.items) : undefined,
    error: query.error,
  };
}

/* What a screen shows for each reading (15 section 1.4). */
export function readingLabel(reading: AssessmentReading): string {
  if (reading === "assessed") return "Assessed";
  return reading === "undated_work" ? "Work recommended, undated" : "Not assessed";
}

/* What a screen shows for a figure of ours the API serves as null because
 * nothing has computed it - said as missing, never filled from a vendor's
 * figure or shown as zero (15 section 1.4). */
export const NOT_COMPUTED = "Not computed";
